import React, { useState, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import Modal from 'react-modal';

const WorkflowEditor = ({ workflow, onSave, onCancel }) => {
  const [nodes, setNodes] = useState(workflow?.nodes || []);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [connections, setConnections] = useState([]);
  const [connecting, setConnecting] = useState(null);
  const canvasRef = useRef(null);

  const icons = [
    '📝', '❓', '✅', '❌', '🔄', '⚠️', '📊', '👤', '🏢', '🏛️', 
    '💼', '📞', '✉️', '🌐', '🔗', '📋', '🎯', '💡', '⚡', '🔍'
  ];

  const nodeTypes = [
    { id: 'start', label: 'Start', color: 'bg-green-500' },
    { id: 'decision', label: 'Decision', color: 'bg-blue-500' },
    { id: 'action', label: 'Action', color: 'bg-purple-500' },
    { id: 'end', label: 'End', color: 'bg-red-500' }
  ];

  useEffect(() => {
    if (workflow) {
      setNodes(workflow.nodes || []);
      // Build connections from node data
      const newConnections = [];
      workflow.nodes?.forEach(node => {
        node.connections?.forEach(targetId => {
          newConnections.push({
            from: node.id,
            to: targetId,
            id: `${node.id}-${targetId}`
          });
        });
      });
      setConnections(newConnections);
    }
  }, [workflow]);

  const createNode = (type) => {
    const newNode = {
      id: `node-${Date.now()}`,
      title: `New ${type}`,
      description: 'Click to edit description',
      icon: '📝',
      position: { x: 100, y: 100 },
      node_type: type,
      options: type === 'decision' ? [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }] : [],
      connections: []
    };
    setNodes([...nodes, newNode]);
    setSelectedNode(newNode);
    setShowNodeModal(true);
  };

  const updateNode = (nodeId, updates) => {
    setNodes(nodes.map(node => 
      node.id === nodeId ? { ...node, ...updates } : node
    ));
  };

  const deleteNode = (nodeId) => {
    setNodes(nodes.filter(node => node.id !== nodeId));
    setConnections(connections.filter(conn => conn.from !== nodeId && conn.to !== nodeId));
  };

  const startConnection = (nodeId) => {
    setConnecting({ from: nodeId, to: null });
  };

  const completeConnection = (nodeId) => {
    if (connecting && connecting.from !== nodeId) {
      const newConnection = {
        from: connecting.from,
        to: nodeId,
        id: `${connecting.from}-${nodeId}`
      };
      
      if (!connections.find(conn => conn.from === newConnection.from && conn.to === newConnection.to)) {
        setConnections([...connections, newConnection]);
        
        // Update node connections
        setNodes(nodes.map(node => 
          node.id === connecting.from 
            ? { ...node, connections: [...(node.connections || []), nodeId] }
            : node
        ));
      }
    }
    setConnecting(null);
  };

  const deleteConnection = (connectionId) => {
    const connection = connections.find(conn => conn.id === connectionId);
    if (connection) {
      setConnections(connections.filter(conn => conn.id !== connectionId));
      setNodes(nodes.map(node => 
        node.id === connection.from 
          ? { ...node, connections: (node.connections || []).filter(id => id !== connection.to) }
          : node
      ));
    }
  };

  const saveWorkflow = () => {
    const updatedWorkflow = {
      ...workflow,
      nodes: nodes,
      start_node_id: nodes.find(n => n.node_type === 'start')?.id || null
    };
    onSave(updatedWorkflow);
  };

  const NodeModal = () => (
    <Modal
      isOpen={showNodeModal}
      onRequestClose={() => setShowNodeModal(false)}
      className="bg-slate-800 rounded-2xl p-6 max-w-2xl mx-auto mt-10 border border-slate-700 max-h-[90vh] overflow-y-auto"
      overlayClassName="fixed inset-0 bg-black/50 flex items-start justify-center p-4"
    >
      {selectedNode && (
        <div>
          <h2 className="text-2xl font-bold text-white mb-6">Edit Node</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Title</label>
              <input
                type="text"
                value={selectedNode.title}
                onChange={(e) => setSelectedNode({...selectedNode, title: e.target.value})}
                className="w-full px-4 py-2 bg-slate-700 text-white rounded-xl border border-slate-600 focus:border-blue-500 focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea
                value={selectedNode.description}
                onChange={(e) => setSelectedNode({...selectedNode, description: e.target.value})}
                className="w-full px-4 py-2 bg-slate-700 text-white rounded-xl border border-slate-600 focus:border-blue-500 focus:outline-none"
                rows={3}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Icon</label>
              <div className="grid grid-cols-10 gap-2 mb-2">
                {icons.map(icon => (
                  <button
                    key={icon}
                    onClick={() => setSelectedNode({...selectedNode, icon})}
                    className={`p-2 rounded-lg text-xl hover:bg-slate-600 transition-colors ${
                      selectedNode.icon === icon ? 'bg-blue-500' : 'bg-slate-700'
                    }`}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Node Type</label>
              <select
                value={selectedNode.node_type}
                onChange={(e) => setSelectedNode({...selectedNode, node_type: e.target.value})}
                className="w-full px-4 py-2 bg-slate-700 text-white rounded-xl border border-slate-600 focus:border-blue-500 focus:outline-none"
              >
                {nodeTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
            </div>
            
            {selectedNode.node_type === 'decision' && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Options</label>
                {selectedNode.options.map((option, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={option.label}
                      onChange={(e) => {
                        const newOptions = [...selectedNode.options];
                        newOptions[index] = {...option, label: e.target.value, value: e.target.value.toLowerCase()};
                        setSelectedNode({...selectedNode, options: newOptions});
                      }}
                      className="flex-1 px-3 py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
                      placeholder="Option text"
                    />
                    <button
                      onClick={() => {
                        const newOptions = selectedNode.options.filter((_, i) => i !== index);
                        setSelectedNode({...selectedNode, options: newOptions});
                      }}
                      className="px-3 py-2 bg-red-500/20 text-red-300 rounded-lg hover:bg-red-500/30 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const newOptions = [...selectedNode.options, { label: 'New Option', value: 'new-option' }];
                    setSelectedNode({...selectedNode, options: newOptions});
                  }}
                  className="px-4 py-2 bg-blue-500/20 text-blue-300 rounded-lg hover:bg-blue-500/30 transition-colors"
                >
                  Add Option
                </button>
              </div>
            )}
          </div>
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setShowNodeModal(false)}
              className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                updateNode(selectedNode.id, selectedNode);
                setShowNodeModal(false);
              }}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:scale-105 transition-transform"
            >
              Save Node
            </button>
          </div>
        </div>
      )}
    </Modal>
  );

  const Node = ({ node }) => {
    const nodeTypeConfig = nodeTypes.find(t => t.id === node.node_type) || nodeTypes[1];
    
    return (
      <Draggable
        position={node.position}
        onDrag={(e, data) => updateNode(node.id, { position: { x: data.x, y: data.y } })}
        handle=".drag-handle"
      >
        <div className="absolute">
          <div className={`
            ${nodeTypeConfig.color} text-white rounded-2xl p-4 min-w-[200px] shadow-lg border-2 border-slate-600
            ${connecting && connecting.from === node.id ? 'ring-2 ring-blue-400' : ''}
            ${connecting && connecting.from !== node.id ? 'ring-2 ring-green-400 cursor-pointer' : ''}
            hover:shadow-xl transition-all duration-200
          `}>
            <div className="drag-handle cursor-move mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{node.icon}</span>
                <span className="font-semibold text-sm">{nodeTypeConfig.label}</span>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    setSelectedNode(node);
                    setShowNodeModal(true);
                  }}
                  className="px-2 py-1 bg-black/20 rounded text-xs hover:bg-black/30 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteNode(node.id)}
                  className="px-2 py-1 bg-red-500/20 rounded text-xs hover:bg-red-500/30 transition-colors"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="text-sm font-medium mb-1">{node.title}</div>
            <div className="text-xs opacity-90 mb-3">{node.description}</div>
            
            <div className="flex justify-between items-center">
              <button
                onClick={() => startConnection(node.id)}
                className="px-3 py-1 bg-black/20 rounded text-xs hover:bg-black/30 transition-colors"
              >
                Connect
              </button>
              
              {connecting && connecting.from !== node.id && (
                <button
                  onClick={() => completeConnection(node.id)}
                  className="px-3 py-1 bg-green-500/20 rounded text-xs hover:bg-green-500/30 transition-colors"
                >
                  Connect Here
                </button>
              )}
            </div>
          </div>
        </div>
      </Draggable>
    );
  };

  const ConnectionLine = ({ connection }) => {
    const fromNode = nodes.find(n => n.id === connection.from);
    const toNode = nodes.find(n => n.id === connection.to);
    
    if (!fromNode || !toNode) return null;
    
    const fromX = fromNode.position.x + 100;
    const fromY = fromNode.position.y + 80;
    const toX = toNode.position.x + 100;
    const toY = toNode.position.y + 40;
    
    return (
      <g>
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
          </marker>
        </defs>
        <line
          x1={fromX}
          y1={fromY}
          x2={toX}
          y2={toY}
          stroke="#64748b"
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
        />
        <circle
          cx={(fromX + toX) / 2}
          cy={(fromY + toY) / 2}
          r="8"
          fill="#ef4444"
          className="cursor-pointer hover:fill-red-400"
          onClick={() => deleteConnection(connection.id)}
        />
        <text
          x={(fromX + toX) / 2}
          y={(fromY + toY) / 2 + 4}
          textAnchor="middle"
          className="text-xs fill-white pointer-events-none"
        >
          ×
        </text>
      </g>
    );
  };

  return (
    <div className="h-full flex flex-col bg-slate-900">
      {/* Toolbar */}
      <div className="bg-slate-800 border-b border-slate-700 p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-white font-semibold">Workflow Editor</h3>
          <div className="flex gap-2">
            {nodeTypes.map(type => (
              <button
                key={type.id}
                onClick={() => createNode(type.id)}
                className={`px-3 py-1 ${type.color} text-white rounded-lg text-sm hover:opacity-80 transition-opacity`}
              >
                + {type.label}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex gap-2">
          {connecting && (
            <button
              onClick={() => setConnecting(null)}
              className="px-4 py-2 bg-red-500/20 text-red-300 rounded-lg hover:bg-red-500/30 transition-colors"
            >
              Cancel Connection
            </button>
          )}
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={saveWorkflow}
            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:scale-105 transition-transform"
          >
            Save Workflow
          </button>
        </div>
      </div>
      
      {/* Canvas */}
      <div className="flex-1 relative overflow-hidden bg-slate-950">
        <div 
          ref={canvasRef}
          className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"
          style={{ 
            backgroundImage: 'radial-gradient(circle, #334155 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
        >
          {/* SVG for connections */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            {connections.map(connection => (
              <ConnectionLine key={connection.id} connection={connection} />
            ))}
          </svg>
          
          {/* Nodes */}
          {nodes.map(node => (
            <Node key={node.id} node={node} />
          ))}
          
          {/* Empty state */}
          {nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-slate-400">
                <div className="text-6xl mb-4">🌳</div>
                <h3 className="text-xl font-semibold mb-2">Create Your First Node</h3>
                <p className="mb-4">Click one of the buttons above to add a node to your workflow</p>
                <div className="flex gap-2 justify-center">
                  {nodeTypes.map(type => (
                    <button
                      key={type.id}
                      onClick={() => createNode(type.id)}
                      className={`px-4 py-2 ${type.color} text-white rounded-lg hover:opacity-80 transition-opacity`}
                    >
                      + {type.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <NodeModal />
    </div>
  );
};

export default WorkflowEditor;